import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Sidebar } from "../components/layout/Sidebar";
import { useSidebar } from "../contexts/SidebarContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { 
  Shield, 
  DollarSign, 
  Search,
  ArrowLeft,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPayments({ user, subscription, onLogout }) {
  const { isCollapsed } = useSidebar();
  const [payments, setPayments] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    try {
      const [paymentsRes, revenueRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/payments?status=${statusFilter}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/revenue`, { withCredentials: true })
      ]);
      setPayments(paymentsRes.data.payments || paymentsRes.data || []);
      setRevenue(revenueRes.data);
    } catch (error) {
      console.error("Failed to fetch payments:", error);
      toast.error("Failed to load payments");
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value || 0);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "paid":
        return <CheckCircle className="w-4 h-4 text-emerald-500" />;
      case "pending":
        return <Clock className="w-4 h-4 text-amber-500" />;
      case "failed":
      case "expired":
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-white/70" />;
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "paid":
        return "bg-emerald-500/20 text-emerald-500 border-emerald-500/40";
      case "pending":
        return "bg-amber-500/20 text-amber-500 border-amber-500/40";
      case "failed":
      case "expired":
        return "bg-red-500/20 text-red-500 border-red-500/40";
      default:
        return "bg-white/20/20 text-white/70 border-white/[0.15]/40";
    }
  };

  if (!user?.is_admin) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-heading font-bold text-white mb-2">Access Denied</h1>
          <p className="text-white/70">You don't have admin privileges.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
      
      <main className={`transition-all duration-300 ${isCollapsed ? 'ml-16' : 'ml-64'} p-8`} data-testid="admin-payments">
        {/* Header */}
        <div className="mb-8">
          <Link to="/admin" className="inline-flex items-center gap-2 text-white/70 hover:text-gold mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Admin
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-emerald-500" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-white uppercase">Payments & Revenue</h1>
              <p className="text-white/70">Track all subscription payments</p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-10 h-10 border-4 border-gold border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Revenue Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
                <div className="flex items-center gap-2 text-white/70 mb-2">
                  <DollarSign className="w-4 h-4" />
                  <span className="text-sm uppercase">Total Revenue</span>
                </div>
                <div className="font-mono text-2xl font-bold text-emerald-500">
                  {formatCurrency(revenue?.total_revenue)}
                </div>
              </div>
              
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
                <div className="flex items-center gap-2 text-white/70 mb-2">
                  <TrendingUp className="w-4 h-4" />
                  <span className="text-sm uppercase">Monthly Revenue</span>
                </div>
                <div className="font-mono text-2xl font-bold text-white">
                  {formatCurrency(revenue?.monthly_revenue)}
                </div>
              </div>
              
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
                <div className="flex items-center gap-2 text-white/70 mb-2">
                  <TrendingUp className="w-4 h-4" />
                  <span className="text-sm uppercase">MRR</span>
                </div>
                <div className="font-mono text-2xl font-bold text-gold">
                  {formatCurrency(revenue?.mrr)}
                </div>
              </div>
              
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
                <div className="flex items-center gap-2 text-white/70 mb-2">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm uppercase">Paid Transactions</span>
                </div>
                <div className="font-mono text-2xl font-bold text-white">
                  {payments.filter(p => p.payment_status === 'paid').length}
                </div>
              </div>
            </div>

            {/* Filter */}
            <div className="flex items-center gap-4 mb-6">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48 bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08]">
                  <SelectItem value="all" className="text-white hover:bg-white/[0.06]">All Statuses</SelectItem>
                  <SelectItem value="paid" className="text-white hover:bg-white/[0.06]">Paid</SelectItem>
                  <SelectItem value="pending" className="text-white hover:bg-white/[0.06]">Pending</SelectItem>
                  <SelectItem value="expired" className="text-white hover:bg-white/[0.06]">Expired</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Payments Table */}
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] overflow-hidden">
              <table className="w-full">
                <thead className="bg-black">
                  <tr>
                    <th className="text-left p-4 text-xs font-bold text-white/70 uppercase tracking-wider">Transaction</th>
                    <th className="text-left p-4 text-xs font-bold text-white/70 uppercase tracking-wider">User</th>
                    <th className="text-left p-4 text-xs font-bold text-white/70 uppercase tracking-wider">Tier</th>
                    <th className="text-left p-4 text-xs font-bold text-white/70 uppercase tracking-wider">Amount</th>
                    <th className="text-left p-4 text-xs font-bold text-white/70 uppercase tracking-wider">Status</th>
                    <th className="text-left p-4 text-xs font-bold text-white/70 uppercase tracking-wider">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.06]">
                  {payments.map((payment) => (
                    <tr key={payment.transaction_id || payment.session_id} className="hover:bg-white/[0.06]/30">
                      <td className="p-4">
                        <p className="text-white font-mono text-sm">
                          {payment.transaction_id || payment.session_id?.slice(0, 20)}...
                        </p>
                      </td>
                      <td className="p-4">
                        <p className="text-white text-sm">{payment.user_email}</p>
                      </td>
                      <td className="p-4">
                        <span className={`text-xs px-2 py-1 ${payment.tier === 'pro' ? 'bg-gold/20 text-gold' : 'bg-white/[0.08] text-white/70'}`}>
                          {payment.tier}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className="font-mono font-bold text-white">{formatCurrency(payment.amount)}</span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(payment.payment_status)}
                          <span className={`text-xs px-2 py-1 border ${getStatusClass(payment.payment_status)}`}>
                            {payment.payment_status}
                          </span>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="text-sm text-white/70">{formatDate(payment.created_at)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {payments.length === 0 && (
                <div className="text-center py-12">
                  <DollarSign className="w-12 h-12 text-white/70 mx-auto mb-3" />
                  <p className="text-white/70">No payments found</p>
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
